(() => {
  const html = document.documentElement;
  html.classList.remove('no-js');
  html.classList.add('js');

  const normalizeOptionValue = (value) => String(value || '').trim().toLowerCase();

  const normalizeMediaSearchValue = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const getColorSearchTerms = (color) => {
    const spaced = normalizeMediaSearchValue(color);
    if (!spaced) return [];
    return Array.from(new Set([
      spaced,
      spaced.replace(/\s+/g, '-'),
      spaced.replace(/\s+/g, '')
    ])).filter(Boolean);
  };

  const getColorMediaIds = (variants, colorIndex, color, grid, selectedMediaId = '') => {
    const colorKey = normalizeOptionValue(color);
    const ids = [];

    if (colorIndex < 0 || !colorKey) return ids;

    variants.forEach((variant) => {
      const variantColor = normalizeOptionValue(variant.options?.[colorIndex]);
      const mediaId = variant.featured_media_id ? String(variant.featured_media_id) : '';
      if (variantColor === colorKey && mediaId && !ids.includes(mediaId)) {
        ids.push(mediaId);
      }
    });

    if (grid) {
      initializeMediaGridTemplates(grid);

      const orderedMediaIds = Array.from(grid._zedMediaTemplates.keys()).map((id) => String(id));

      const allColorVariantMediaIds = [];
      variants.forEach((variant) => {
        const variantColor = normalizeOptionValue(variant.options?.[colorIndex]);
        const mediaId = variant.featured_media_id ? String(variant.featured_media_id) : '';
        if (variantColor && mediaId && !allColorVariantMediaIds.includes(mediaId)) {
          allColorVariantMediaIds.push(mediaId);
        }
      });

      const baseColorMediaIds = orderedMediaIds.filter((mediaId) => allColorVariantMediaIds.includes(mediaId));
      const selectedBoundaryIds = ids.map((id) => String(id));
      const primaryId = selectedMediaId && orderedMediaIds.includes(String(selectedMediaId))
        ? String(selectedMediaId)
        : selectedBoundaryIds.find((id) => orderedMediaIds.includes(String(id)));

      const selectedBaseIndex = primaryId ? baseColorMediaIds.indexOf(primaryId) : -1;
      const baseCount = baseColorMediaIds.length;
      const baseStartIndex = baseCount ? orderedMediaIds.indexOf(baseColorMediaIds[0]) : -1;

      if (selectedBaseIndex >= 0 && baseCount > 0 && baseStartIndex >= 0) {
        orderedMediaIds.forEach((mediaId, mediaIndex) => {
          const relativeIndex = mediaIndex - baseStartIndex;
          if (relativeIndex >= 0 && relativeIndex % baseCount === selectedBaseIndex && !ids.includes(String(mediaId))) {
            ids.push(String(mediaId));
          }
        });
      }

      const searchTerms = getColorSearchTerms(color);
      if (searchTerms.length) {
        grid._zedMediaTemplates.forEach((template, mediaId) => {
          const searchText = template.searchText || '';
          const isColorMatch = searchTerms.some((term) => searchText.includes(term));
          if (isColorMatch && mediaId && !ids.includes(String(mediaId))) {
            ids.push(String(mediaId));
          }
        });
      }
    }

    return ids;
  };

  const initializeMediaGridTemplates = (grid) => {
    if (grid._zedMediaTemplates && grid._zedMediaSlots) return;

    const items = Array.from(grid.querySelectorAll('[data-media-id]'));
    grid._zedMediaTemplates = new Map();
    grid._zedMediaSlots = items;

    items.forEach((item, index) => {
      const mediaId = String(item.dataset.mediaId || '');
      item.dataset.displaySlot = String(index);
      item.dataset.originalMediaId = mediaId;
      item.classList.add('product-media-item--slot');

      if (mediaId) {
        const image = item.querySelector('img');
        const searchText = normalizeMediaSearchValue([
          item.dataset.mediaAlt,
          item.dataset.mediaPosition,
          image?.alt,
          image?.getAttribute('src'),
          image?.getAttribute('srcset')
        ].filter(Boolean).join(' '));

        grid._zedMediaTemplates.set(mediaId, {
          id: mediaId,
          html: item.innerHTML,
          className: item.className,
          searchText
        });
      }
    });
  };

  const getMediaImageFromTemplate = (template) => {
    if (!template?.html) return null;
    const wrapper = document.createElement('div');
    wrapper.innerHTML = template.html;
    return wrapper.querySelector('img');
  };

  const preloadMediaTemplates = (grid, mediaIds) => {
    initializeMediaGridTemplates(grid);

    const loads = mediaIds.map((id) => {
      const template = grid._zedMediaTemplates.get(String(id));
      const image = getMediaImageFromTemplate(template);
      if (!image) return Promise.resolve();

      return new Promise((resolve) => {
        const preload = new Image();
        const done = () => resolve();
        const timeout = window.setTimeout(done, 900);

        preload.decoding = 'async';
        preload.onload = () => {
          window.clearTimeout(timeout);
          if (preload.decode) {
            preload.decode().then(done).catch(done);
          } else {
            done();
          }
        };
        preload.onerror = () => {
          window.clearTimeout(timeout);
          done();
        };

        const srcset = image.getAttribute('srcset');
        const sizes = image.getAttribute('sizes');
        if (srcset) preload.srcset = srcset;
        if (sizes) preload.sizes = sizes;
        preload.src = image.currentSrc || image.getAttribute('src') || '';
      });
    });

    return Promise.race([
      Promise.all(loads),
      new Promise((resolve) => window.setTimeout(resolve, 950))
    ]);
  };

  const swapMediaSlot = (slot, template, mediaId) => {
    if (!slot || !template) return;

    const targetId = String(mediaId || '');
    const alreadyActive = String(slot.dataset.mediaId || '') === targetId && !slot.classList.contains('is-hidden');
    slot.classList.remove('is-hidden');

    if (alreadyActive) return;

    const currentImage = slot.querySelector('img:not(.product-media-item__incoming)') || slot.querySelector('img');
    const nextImage = getMediaImageFromTemplate(template);

    if (!currentImage || !nextImage) {
      slot.innerHTML = template.html;
      slot.dataset.mediaId = targetId;
      return;
    }

    slot.classList.remove('is-media-swap-active');
    slot.querySelectorAll('.product-media-item__incoming').forEach((image) => image.remove());
    slot.querySelectorAll('.product-media-item__current').forEach((image) => image.classList.remove('product-media-item__current'));
    currentImage.classList.add('product-media-item__current');

    const incoming = nextImage.cloneNode(true);
    incoming.classList.add('product-media-item__incoming');
    incoming.loading = 'eager';
    incoming.decoding = 'async';
    incoming.setAttribute('aria-hidden', 'true');
    slot.appendChild(incoming);
    slot.dataset.mediaId = targetId;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        slot.classList.add('is-media-swap-active');
      });
    });

    incoming.addEventListener('load', () => {
      const grid = slot.closest('[data-product-media-gallery]');
      if (grid) window.requestAnimationFrame(() => updateProductGalleryArrowPosition(grid));
    }, { once: true });

    window.clearTimeout(slot._zedMediaSlotTimer);
    slot._zedMediaSlotTimer = window.setTimeout(() => {
      slot.innerHTML = template.html;
      slot.dataset.mediaId = targetId;
      slot.classList.remove('is-media-swap-active');
      const grid = slot.closest('[data-product-media-gallery]');
      if (grid) window.requestAnimationFrame(() => updateProductGalleryArrowPosition(grid));
    }, 460);
  };

  const getRenderedProductMediaRect = (container, mediaElement) => {
    if (!container) return null;

    const boxRect = container.getBoundingClientRect();
    if (!boxRect.width || !boxRect.height || !mediaElement) return boxRect;

    if (mediaElement.tagName !== 'IMG' || !mediaElement.naturalWidth || !mediaElement.naturalHeight) {
      return boxRect;
    }

    const objectFit = window.getComputedStyle(mediaElement).objectFit || 'fill';
    if (objectFit !== 'contain' && objectFit !== 'scale-down') {
      return boxRect;
    }

    const imageRatio = mediaElement.naturalWidth / mediaElement.naturalHeight;
    const boxRatio = boxRect.width / boxRect.height;

    let renderedWidth = boxRect.width;
    let renderedHeight = boxRect.height;

    if (boxRatio > imageRatio) {
      renderedHeight = boxRect.height;
      renderedWidth = boxRect.height * imageRatio;
    } else {
      renderedWidth = boxRect.width;
      renderedHeight = boxRect.width / imageRatio;
    }

    return {
      left: boxRect.left + (boxRect.width - renderedWidth) / 2,
      right: boxRect.left + (boxRect.width + renderedWidth) / 2,
      top: boxRect.top + (boxRect.height - renderedHeight) / 2,
      bottom: boxRect.top + (boxRect.height + renderedHeight) / 2,
      width: renderedWidth,
      height: renderedHeight
    };
  };

  const updateProductGalleryArrowPosition = (grid) => {
    if (!grid) return;
    const primary = grid.querySelector('.product-media-item.is-primary-media:not(.is-hidden)');
    if (!primary) return;

    const mediaElement = primary.querySelector('img, video, iframe') || primary;
    const gridRect = grid.getBoundingClientRect();
    const mediaRect = getRenderedProductMediaRect(primary, mediaElement) || primary.getBoundingClientRect();
    const center = (mediaRect.top - gridRect.top) + (mediaRect.height / 2);
    const left = mediaRect.left - gridRect.left;
    const right = mediaRect.right - gridRect.left;

    if (Number.isFinite(center) && center > 0) {
      grid.style.setProperty('--zed-gallery-arrow-y', `${center}px`, 'important');
    }

    if (Number.isFinite(left)) {
      grid.style.setProperty('--zed-gallery-arrow-left', `${left}px`, 'important');
    }

    if (Number.isFinite(right) && right > 0) {
      grid.style.setProperty('--zed-gallery-arrow-right', `${right}px`, 'important');
    }
  };

  const renderProductMediaSlots = (grid, mediaIds, primarySlot = 0) => {
    initializeMediaGridTemplates(grid);

    const slots = grid._zedMediaSlots || [];
    const visibleIds = mediaIds.filter((id) => grid._zedMediaTemplates.has(String(id)));
    const primaryIndex = Math.min(Math.max(primarySlot, 0), Math.max(visibleIds.length - 1, 0));
    const primaryId = visibleIds[primaryIndex];
    const displayIds = primaryId
      ? [primaryId, ...visibleIds.filter((id, index) => index !== primaryIndex)]
      : visibleIds;

    grid._zedActiveMediaIds = visibleIds;
    grid.classList.toggle('is-gallery-single', visibleIds.length <= 1);

    slots.forEach((slot, index) => {
      slot.classList.remove('is-primary-media');

      const mediaId = displayIds[index];
      if (!mediaId) {
        slot.classList.add('is-hidden');
        return;
      }

      const template = grid._zedMediaTemplates.get(String(mediaId));
      swapMediaSlot(slot, template, mediaId);
    });

    const primary = slots[0];
    if (primary && !primary.classList.contains('is-hidden')) primary.classList.add('is-primary-media');

    grid.dataset.currentMediaSlot = String(primaryIndex);
    window.requestAnimationFrame(() => {
      updateProductGalleryArrowPosition(grid);
      window.setTimeout(() => updateProductGalleryArrowPosition(grid), 120);
      window.setTimeout(() => updateProductGalleryArrowPosition(grid), 520);
    });
  };

  const stepProductMediaGallery = (grid, direction = 1) => {
    if (!grid) return;
    initializeMediaGridTemplates(grid);

    const activeIds = (grid._zedActiveMediaIds || Array.from(grid._zedMediaTemplates.keys()))
      .filter((id) => grid._zedMediaTemplates.has(String(id)));
    if (activeIds.length <= 1) return;

    const current = Number(grid.dataset.currentMediaSlot || 0);
    const next = (current + direction + activeIds.length) % activeIds.length;
    renderProductMediaSlots(grid, activeIds, next);
  };

  const initializeProductMediaGallery = (grid) => {
    if (!grid || grid._zedProductGalleryInitialized) return;
    grid._zedProductGalleryInitialized = true;

    let touchStartX = 0;
    let touchStartY = 0;

    grid.addEventListener('touchstart', (event) => {
      const touch = event.touches?.[0];
      if (!touch) return;
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
    }, { passive: true });

    grid.addEventListener('touchend', (event) => {
      const touch = event.changedTouches?.[0];
      if (!touch) return;

      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;
      if (Math.abs(deltaX) < 42 || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) return;

      stepProductMediaGallery(grid, deltaX < 0 ? 1 : -1);
    }, { passive: true });
  };

  const updateProductMedia = (select) => {
    const selected = select.options[select.selectedIndex];
    const mediaId = selected?.dataset?.mediaId;

    const section = select.closest('.section--product');
    const grid = section?.querySelector('.product-media-grid');
    if (!grid) return;

    initializeMediaGridTemplates(grid);

    const form = select.closest('.product-form');
    const picker = form?.querySelector('[data-variant-picker]');
    const variants = getVariantData(picker);
    const selectedVariant = variants.find((variant) => String(variant.id) === selected?.value);
    const colorGroup = picker?.querySelector('.variant-picker__group--color');
    const colorIndex = colorGroup ? Number(colorGroup.dataset.optionIndex) : -1;
    const selectedColor = colorIndex >= 0 ? selectedVariant?.options?.[colorIndex] : null;
    const selectedColorMediaIds = getColorMediaIds(variants, colorIndex, selectedColor, grid, mediaId);
    const validSelectedColorMediaIds = selectedColorMediaIds.filter((id) => grid._zedMediaTemplates.has(String(id)));
    const selectedVariantMediaIds = mediaId && grid._zedMediaTemplates.has(String(mediaId)) ? [mediaId] : [];

    const activeMediaIds = validSelectedColorMediaIds.length
      ? validSelectedColorMediaIds
      : selectedVariantMediaIds.length
        ? selectedVariantMediaIds
        : Array.from(grid._zedMediaTemplates.keys()).slice(0, 1);

    const primarySlot = Number(grid.dataset.currentMediaSlot || 0);
    const isInitialMediaRender = grid.dataset.mediaInitialized !== 'true';

    if (isInitialMediaRender) {
      renderProductMediaSlots(grid, activeMediaIds, primarySlot);
      grid.dataset.mediaInitialized = 'true';
      preloadMediaTemplates(grid, activeMediaIds);
    } else {
      const switchId = `${Date.now()}-${Math.random()}`;
      grid.dataset.mediaSwitchId = switchId;
      grid.classList.add('is-seamless-color-switching');

      preloadMediaTemplates(grid, activeMediaIds).then(() => {
        if (grid.dataset.mediaSwitchId !== switchId) return;
        renderProductMediaSlots(grid, activeMediaIds, primarySlot);

        window.clearTimeout(grid._zedMediaSwitchTimer);
        grid._zedMediaSwitchTimer = window.setTimeout(() => {
          grid.classList.remove('is-seamless-color-switching');
        }, 380);
      });
    }

    if (selectedColor) grid.dataset.currentColor = selectedColor;
  };

  const updateProductSummary = (select) => {
    const selected = select.options[select.selectedIndex];
    const form = select.closest('.product-form');
    if (!form || !selected) return;

    const submit = form.querySelector('button[name="add"]');
    if (submit) {
      submit.disabled = selected.disabled;
      submit.textContent = selected.disabled ? window.themeStrings?.soldOut || 'Sold out' : window.themeStrings?.addToCart || 'Add to cart';
    }

    const summary = form.closest('.product-summary');
    const price = summary?.querySelector('[data-price]');
    if (price && selected.dataset.price) {
      const compare = selected.dataset.compare;
      price.classList.toggle('price--sale', Boolean(compare));
      price.innerHTML = compare
        ? `<span class="price__sale">${selected.dataset.price}</span><s class="price__compare">${compare}</s>`
        : `<span>${selected.dataset.price}</span>`;
    }

    updateProductMedia(select);
  };

  const getVariantData = (picker) => {
    const source = picker?.querySelector('[data-variant-data]');
    if (!source) return [];
    try {
      return JSON.parse(source.textContent || '[]');
    } catch (error) {
      return [];
    }
  };

  const getSelectedOptions = (picker) => {
    return Array.from(picker.querySelectorAll('[data-option-group]')).reduce((options, group) => {
      const index = Number(group.dataset.optionIndex);
      const selected = group.querySelector('[data-option-button].is-selected');
      if (selected) options[index] = selected.dataset.optionValue;
      return options;
    }, []);
  };

  const syncPickerToVariant = (picker, variant) => {
    if (!picker || !variant) return;

    picker.querySelectorAll('[data-option-group]').forEach((group) => {
      const optionIndex = Number(group.dataset.optionIndex);
      const value = variant.options[optionIndex];
      if (!value) return;

      group.querySelectorAll('[data-option-button]').forEach((optionButton) => {
        const isSelected = optionButton.dataset.optionValue === value;
        optionButton.classList.toggle('is-selected', isSelected);
        optionButton.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });

      const currentLabel = group.querySelector('[data-option-current]');
      if (currentLabel) currentLabel.textContent = value;
    });
  };

  const findBestVariant = (variants, selectedOptions, changedIndex) => {
    const exactMatch = variants.find((variant) => {
      return variant.available && variant.options.length === selectedOptions.length && variant.options.every((option, index) => selectedOptions[index] === option);
    });
    if (exactMatch) return exactMatch;

    if (Number.isInteger(changedIndex) && selectedOptions[changedIndex]) {
      const sameChangedOption = variants.find((variant) => {
        return variant.available && variant.options[changedIndex] === selectedOptions[changedIndex];
      });
      if (sameChangedOption) return sameChangedOption;
    }

    return variants.find((variant) => variant.available) || null;
  };

  const refreshPickerAvailability = (picker, variants) => {
    const selectedOptions = getSelectedOptions(picker);

    picker.querySelectorAll('[data-option-group]').forEach((group) => {
      const optionIndex = Number(group.dataset.optionIndex);
      group.querySelectorAll('[data-option-button]').forEach((button) => {
        const trialOptions = selectedOptions.slice();
        trialOptions[optionIndex] = button.dataset.optionValue;
        const hasAvailableMatch = variants.some((variant) => {
          return variant.available && variant.options.every((option, index) => {
            return index === optionIndex || !trialOptions[index] || trialOptions[index] === option;
          });
        });
        button.classList.toggle('is-unavailable', !hasAvailableMatch);
        button.setAttribute('aria-disabled', hasAvailableMatch ? 'false' : 'true');
      });
    });
  };



  const formatCartMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || document.documentElement.dataset.currency || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  };

  const updateCartCount = (itemCount) => {
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = itemCount > 0 ? String(itemCount) : '';
      count.classList.toggle('cart-count--empty', itemCount <= 0);
    });

    document.querySelectorAll('.site-header__cart').forEach((cartLink) => {
      cartLink.classList.toggle('site-header__cart--has-items', itemCount > 0);
    });
  };

  const refreshCartDom = (form, cart) => {
    const items = Array.from(form.querySelectorAll('[data-cart-item]'));

    items.forEach((itemElement, index) => {
      const cartItem = cart.items[index];
      if (!cartItem) {
        itemElement.remove();
        return;
      }

      const input = itemElement.querySelector('[data-quantity-input]');
      if (input && document.activeElement !== input) {
        input.value = String(cartItem.quantity);
      }

      const price = itemElement.querySelector('[data-cart-line-price]');
      if (price) price.textContent = formatCartMoney(cartItem.final_line_price);
    });

    const subtotal = form.querySelector('[data-cart-subtotal]');
    if (subtotal) subtotal.textContent = formatCartMoney(cart.total_price);

    updateCartCount(cart.item_count);

    if (cart.item_count === 0) {
      window.location.reload();
    }
  };

  const submitCartUpdates = (() => {
    let timer = null;
    let controller = null;

    return (form) => {
      if (!form || form.dataset.cartCheckout === 'true') return;
      clearTimeout(timer);

      timer = setTimeout(async () => {
        if (controller) controller.abort();
        controller = new AbortController();

        try {
          const response = await fetch('/cart/update.js', {
            method: 'POST',
            body: new FormData(form),
            headers: { 'Accept': 'application/json' },
            signal: controller.signal
          });

          if (!response.ok) throw new Error('Cart update failed');

          const cart = await response.json();
          refreshCartDom(form, cart);
        } catch (error) {
          if (error.name !== 'AbortError') {
            form.submit();
          }
        }
      }, 120);
    };
  })();


  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-variant-select]');
    if (!select) return;
    updateProductSummary(select);
  });

  document.addEventListener('click', (event) => {
    const previous = event.target.closest('[data-product-gallery-prev]');
    const next = event.target.closest('[data-product-gallery-next]');
    if (!previous && !next) return;

    const grid = event.target.closest('[data-product-media-gallery]');
    if (!grid) return;

    event.preventDefault();
    stepProductMediaGallery(grid, previous ? -1 : 1);
  });


  document.addEventListener('input', (event) => {
    const input = event.target.closest('.cart-form [name="updates[]"]');
    if (!input) return;
    submitCartUpdates(input.closest('.cart-form'));
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('.cart-form [name="updates[]"]');
    if (!input) return;
    submitCartUpdates(input.closest('.cart-form'));
  });

  document.addEventListener('click', (event) => {
    const checkoutButton = event.target.closest('.cart-form button[name="checkout"]');
    if (!checkoutButton) return;
    const form = checkoutButton.closest('.cart-form');
    if (form) form.dataset.cartCheckout = 'true';
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-option-button]');
    if (!button) return;

    const picker = button.closest('[data-variant-picker]');
    const group = button.closest('[data-option-group]');
    const form = button.closest('.product-form');
    const select = form?.querySelector('[data-variant-select]');
    if (!picker || !group || !select) return;

    group.querySelectorAll('[data-option-button]').forEach((optionButton) => {
      const isSelected = optionButton === button;
      optionButton.classList.toggle('is-selected', isSelected);
      optionButton.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    const currentLabel = group.querySelector('[data-option-current]');
    if (currentLabel) currentLabel.textContent = button.dataset.optionValue;

    const variants = getVariantData(picker);
    const selectedOptions = getSelectedOptions(picker);
    const changedIndex = Number(group.dataset.optionIndex);
    const matchedVariant = findBestVariant(variants, selectedOptions, changedIndex);

    if (matchedVariant) {
      syncPickerToVariant(picker, matchedVariant);
      select.value = String(matchedVariant.id);
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    refreshPickerAvailability(picker, variants);
  });



  document.addEventListener('click', (event) => {
    const removeLink = event.target.closest('[data-cart-remove]');
    if (!removeLink) return;

    const form = removeLink.closest('.cart-form');
    const item = removeLink.closest('[data-cart-item]');
    const input = item?.querySelector('[data-quantity-input]');
    if (!form || !input) return;

    event.preventDefault();
    input.value = '0';
    submitCartUpdates(form);
  });


  document.addEventListener('click', (event) => {
    const quantityButton = event.target.closest('[data-quantity-button]');
    if (!quantityButton) return;

    const stepper = quantityButton.closest('[data-quantity-stepper]');
    const input = stepper?.querySelector('[data-quantity-input]');
    if (!input) return;

    const change = Number(quantityButton.dataset.quantityChange || 0);
    const min = input.min ? Number(input.min) : 1;
    const max = input.max ? Number(input.max) : Infinity;
    const current = Number(input.value || min);
    const next = Math.max(min, Math.min(max, current + change));

    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });


  const applyHomeGridLift = () => {
    const path = window.location.pathname.replace(/\/+$/, '');
    const isHome = document.body.classList.contains('template-index') || path === '';
    if (!isHome) return;

    const grid = document.querySelector('#MainContent .home-product-grid, #MainContent .section--home-products .product-grid, #MainContent .shopify-section--merchandise-grid .product-grid');
    if (!grid) return;

    const lift = window.matchMedia('(max-width: 520px)').matches
      ? 13
      : window.matchMedia('(max-width: 860px)').matches
        ? 8
        : 14;

    grid.style.setProperty('transform', `translate3d(0, ${lift}px, 0)`, 'important');
    grid.style.setProperty('margin-bottom', `${lift}px`, 'important');
    grid.style.setProperty('will-change', 'transform', 'important');

    const section = grid.closest('.section--home-products, .shopify-section--merchandise-grid');
    if (section) {
      section.style.setProperty('padding-top', '0px', 'important');
      section.style.setProperty('margin-top', '0px', 'important');
    }
  };

  applyHomeGridLift();
  window.addEventListener('load', applyHomeGridLift);
  window.addEventListener('resize', applyHomeGridLift);

  window.addEventListener('resize', () => {
    document.querySelectorAll('[data-product-media-gallery]').forEach((grid) => updateProductGalleryArrowPosition(grid));
  });

  window.addEventListener('load', () => {
    document.querySelectorAll('[data-product-media-gallery]').forEach((grid) => updateProductGalleryArrowPosition(grid));
  });


  document.querySelectorAll('[data-product-media-gallery]').forEach((grid) => {
    initializeProductMediaGallery(grid);
  });

  document.querySelectorAll('[data-variant-picker]').forEach((picker) => {
    refreshPickerAvailability(picker, getVariantData(picker));
    const select = picker.closest('.product-form')?.querySelector('[data-variant-select]');
    if (select) updateProductSummary(select);
  });

  document.querySelectorAll('[data-product-media-gallery]:not([data-media-initialized="true"])').forEach((grid) => {
    initializeMediaGridTemplates(grid);
    const section = grid.closest('.section--product');
    const select = section?.querySelector('[data-variant-select]');
    const selected = select?.options?.[select.selectedIndex];
    const selectedMediaId = selected?.dataset?.mediaId;
    renderProductMediaSlots(grid, selectedMediaId ? [selectedMediaId] : Array.from(grid._zedMediaTemplates.keys()), Number(grid.dataset.currentMediaSlot || 0));
    grid.dataset.mediaInitialized = 'true';
  });
})();


// v138 back arrows: use browser history when available, otherwise fall back to the link href.
document.addEventListener('click', (event) => {
  const backLink = event.target.closest('[data-zed-back-link]');
  if (!backLink) return;

  if (window.history.length > 1 && document.referrer) {
    event.preventDefault();
    window.history.back();
  }
});

// v157 TikTok dynamic event bridge:
// Replaces the prior event bridge with dynamic TikTok events using real product/cart/search data.
// PII identify data is hashed client-side with SHA-256 when logged-in customer data is available.
(() => {
  const TIKTOK_RETRY_LIMIT = 40;
  const TIKTOK_RETRY_DELAY = 250;
  const firedOnce = new Set();

  const toMoneyValue = (cents) => {
    const value = Number(cents || 0) / 100;
    return Number.isFinite(value) ? Number(value.toFixed(2)) : 0;
  };

  const getCurrency = (fallback) => {
    return fallback
      || window.Shopify?.currency?.active
      || document.documentElement.dataset.currency
      || 'USD';
  };

  const cleanPayload = (payload = {}) => {
    const cleaned = { ...payload };
    Object.keys(cleaned).forEach((key) => {
      const value = cleaned[key];
      if (value === undefined || value === null || value === '') delete cleaned[key];
      if (Array.isArray(value) && !value.length) delete cleaned[key];
    });
    return cleaned;
  };

  const whenTikTokReady = (callback) => {
    let attempts = 0;
    const run = () => {
      if (window.ttq && typeof window.ttq.track === 'function') {
        callback(window.ttq);
        return;
      }

      attempts += 1;
      if (attempts <= TIKTOK_RETRY_LIMIT) {
        window.setTimeout(run, TIKTOK_RETRY_DELAY);
      }
    };
    run();
  };

  const trackTikTok = (eventName, payload = {}, options = {}) => {
    const normalizedPayload = cleanPayload(payload);
    const onceKey = options.onceKey ? `${eventName}:${options.onceKey}` : null;

    if (onceKey && firedOnce.has(onceKey)) return;
    if (onceKey) firedOnce.add(onceKey);

    whenTikTokReady((ttq) => {
      try {
        ttq.track(eventName, normalizedPayload);
      } catch (error) {
        // Do not interrupt the customer journey if the third-party tracker errors.
      }
    });
  };

  const normalizeForHash = (value, type) => {
    if (!value) return '';
    const normalized = String(value).trim().toLowerCase();
    if (type === 'phone_number') return normalized.replace(/[^\d+]/g, '');
    return normalized;
  };

  const sha256 = async (value) => {
    if (!value || !window.crypto?.subtle || !window.TextEncoder) return '';
    const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
  };

  const identifyCustomerIfAvailable = async () => {
    const source = document.getElementById('ZedTikTokCustomerData');
    if (!source) return;

    let customer = {};
    try {
      customer = JSON.parse(source.textContent || '{}');
    } catch (error) {
      return;
    }

    const email = normalizeForHash(customer.email, 'email');
    const phone = normalizeForHash(customer.phone_number, 'phone_number');
    const externalId = normalizeForHash(customer.external_id, 'external_id');

    const [hashedEmail, hashedPhone, hashedExternalId] = await Promise.all([
      sha256(email),
      sha256(phone),
      sha256(externalId)
    ]);

    const payload = cleanPayload({
      email: hashedEmail,
      phone_number: hashedPhone,
      external_id: hashedExternalId
    });

    if (!Object.keys(payload).length) return;

    whenTikTokReady((ttq) => {
      try {
        ttq.identify(payload);
      } catch (error) {
        // Keep tracking resilient if identify is unavailable or blocked.
      }
    });
  };

  const getVariantDataFromProduct = (productRoot) => {
    const source = productRoot?.querySelector('[data-variant-data]');
    if (!source) return [];
    try {
      return JSON.parse(source.textContent || '[]');
    } catch (error) {
      return [];
    }
  };

  const getSelectedVariantData = (productRoot) => {
    const select = productRoot?.querySelector('[data-variant-select]');
    const selectedId = select?.value ? String(select.value) : '';
    const variants = getVariantDataFromProduct(productRoot);
    return variants.find((variant) => String(variant.id) === selectedId) || variants[0] || null;
  };

  const getProductPayload = (productRoot, status = 'submitted') => {
    if (!productRoot) return null;

    const variant = getSelectedVariantData(productRoot);
    const quantityInput = productRoot.querySelector('[name="quantity"]');
    const numItems = Math.max(1, Number(quantityInput?.value || 1));
    const productId = productRoot.dataset.productId || variant?.id;
    const contentId = variant?.sku || variant?.id || productId;
    const contentName = productRoot.dataset.productTitle || document.querySelector('.product-summary h1')?.textContent?.trim() || document.title;
    const currency = getCurrency(productRoot.dataset.productCurrency);
    const itemPrice = toMoneyValue(variant?.price_cents);
    const value = Number((itemPrice * numItems).toFixed(2));

    return cleanPayload({
      contents: [{
        content_id: String(contentId || ''),
        content_type: 'product',
        content_name: contentName,
        num_items: numItems
      }],
      value,
      currency,
      status
    });
  };

  const getGenericPagePayload = () => {
    return cleanPayload({
      contents: [{
        content_id: window.location.pathname || 'page',
        content_type: 'product_group',
        content_name: document.title || window.location.pathname
      }],
      value: 0,
      currency: getCurrency()
    });
  };

  const getCartPayload = (status = 'submitted') => {
    const source = document.querySelector('[data-cart-tiktok]');
    if (!source) return null;

    try {
      const cart = JSON.parse(source.textContent || '{}');
      const contents = Array.isArray(cart.contents)
        ? cart.contents.map((item) => ({
            content_id: String(item.content_id || ''),
            content_type: item.content_type || 'product',
            content_name: item.content_name,
            num_items: Number(item.quantity || 1)
          }))
        : [];

      return cleanPayload({
        contents,
        value: toMoneyValue(cart.value_cents),
        currency: getCurrency(cart.currency),
        status
      });
    } catch (error) {
      return null;
    }
  };

  const getCheckoutPayload = (status = 'submitted') => {
    const checkout = window.Shopify?.checkout || window.Shopify?.Checkout || window.Shopify?.order;
    if (!checkout) return null;

    const lineItems = checkout.line_items || checkout.lineItems || checkout.items || [];
    const contents = Array.isArray(lineItems)
      ? lineItems.map((item) => ({
          content_id: String(item.variant_id || item.variantId || item.product_id || item.productId || item.sku || ''),
          content_type: 'product',
          content_name: item.title || item.name || item.product_title || item.productTitle,
          num_items: Number(item.quantity || 1)
        }))
      : [];

    return cleanPayload({
      contents,
      value: Number(checkout.total_price || checkout.totalPrice || checkout.total || 0),
      currency: getCurrency(checkout.currency || checkout.currencyCode),
      status,
      order_id: checkout.order_id || checkout.orderId || checkout.id || undefined
    });
  };

  const trackViewContent = () => {
    const productRoot = document.querySelector('[data-tiktok-product]');
    const payload = productRoot ? getProductPayload(productRoot) : null;
    if (payload) {
      trackTikTok('ViewContent', payload, { onceKey: `view-${payload.contents?.[0]?.content_id}-${window.location.pathname}` });
    }
  };

  const trackSearch = () => {
    const params = new URLSearchParams(window.location.search);
    const searchString = params.get('q') || params.get('query') || params.get('search');
    const isSearchPage = document.body.classList.contains('template-search') || /^\/search/i.test(window.location.pathname);
    if (!isSearchPage || !searchString) return;

    const payload = cleanPayload({
      ...getGenericPagePayload(),
      search_string: searchString
    });

    payload.contents = [{
      content_id: `search-${searchString}`,
      content_type: 'product_group',
      content_name: `Search: ${searchString}`
    }];

    trackTikTok('Search', payload, { onceKey: `search-${searchString}` });
  };

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest('.product-form button[name="add"]');
    if (!addButton || addButton.disabled) return;

    const productRoot = addButton.closest('[data-tiktok-product]');
    const payload = getProductPayload(productRoot);
    if (payload) {
      trackTikTok('AddToCart', payload);
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('.product-form');
    if (!form || form._zedTikTokAddTracked) return;

    const submitter = event.submitter || document.activeElement;
    if (submitter && submitter.name && submitter.name !== 'add') return;

    form._zedTikTokAddTracked = true;
    const productRoot = form.closest('[data-tiktok-product]');
    const payload = getProductPayload(productRoot);
    if (payload) {
      trackTikTok('AddToCart', payload);
    }

    window.setTimeout(() => { form._zedTikTokAddTracked = false; }, 1500);
  });

  document.addEventListener('click', (event) => {
    const wishlistButton = event.target.closest('[data-tiktok-wishlist], [data-wishlist-add], .wishlist-button, button[name="add_to_wishlist"], a[href*="wishlist"]');
    if (!wishlistButton) return;

    const productRoot = wishlistButton.closest('[data-tiktok-product]') || document.querySelector('[data-tiktok-product]');
    const payload = getProductPayload(productRoot) || getGenericPagePayload();
    trackTikTok('AddToWishlist', payload);
  });

  const trackCheckoutStart = () => {
    const payload = getCartPayload() || getGenericPagePayload();
    trackTikTok('InitiateCheckout', payload);
  };

  document.addEventListener('click', (event) => {
    const checkoutButton = event.target.closest('.cart-form button[name="checkout"], .cart-summary button[name="checkout"], a[href*="/checkout"]');
    if (!checkoutButton) return;
    trackCheckoutStart();
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('.cart-form');
    if (!form || form._zedTikTokCheckoutTracked) return;

    const submitter = event.submitter || document.activeElement;
    if (submitter?.name === 'checkout' || form.dataset.cartCheckout === 'true') {
      form._zedTikTokCheckoutTracked = true;
      trackCheckoutStart();
      window.setTimeout(() => { form._zedTikTokCheckoutTracked = false; }, 1500);
    }
  });

  document.addEventListener('click', (event) => {
    const paymentButton = event.target.closest('[data-tiktok-payment], button[name*="payment"], a[href*="payment"]');
    if (!paymentButton) return;

    const payload = getCartPayload() || getCheckoutPayload() || getGenericPagePayload();
    trackTikTok('AddPaymentInfo', payload);
  });

  document.addEventListener('submit', (event) => {
    const registrationForm = event.target.closest('form[action*="/account"], form#create_customer, form[id*="create_customer"]');
    if (!registrationForm || registrationForm._zedTikTokRegistrationTracked) return;

    const formText = `${registrationForm.id || ''} ${registrationForm.getAttribute('action') || ''} ${registrationForm.querySelector('[name="form_type"]')?.value || ''}`.toLowerCase();
    if (!formText.includes('create_customer') && !formText.includes('register')) return;

    registrationForm._zedTikTokRegistrationTracked = true;
    trackTikTok('CompleteRegistration', getGenericPagePayload());
    window.setTimeout(() => { registrationForm._zedTikTokRegistrationTracked = false; }, 1500);
  });

  const trackOrderEventsIfAvailable = () => {
    const isPossibleOrderPage = /\/thank_you|\/orders\//i.test(window.location.pathname);
    if (!isPossibleOrderPage) return;

    const payload = getCheckoutPayload();
    if (!payload) return;

    const onceKey = payload.order_id || window.location.pathname;
    trackTikTok('PlaceAnOrder', payload, { onceKey });
    trackTikTok('Purchase', payload, { onceKey });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      identifyCustomerIfAvailable();
      trackViewContent();
      trackSearch();
      trackOrderEventsIfAvailable();
    });
  } else {
    identifyCustomerIfAvailable();
    trackViewContent();
    trackSearch();
    trackOrderEventsIfAvailable();
  }
})();

// v173 product image zoom:
(() => {
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 4.5;
  let overlay = null;
  let viewport = null;
  let zoomImage = null;
  let scale = 1;
  let translateX = 0;
  let translateY = 0;
  let lastPointer = null;
  let pinchStartDistance = 0;
  let pinchStartScale = 1;
  const activePointers = new Map();

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const getVisibleMediaImage = (item) => {
    if (!item) return null;
    return item.querySelector('.product-media-item__incoming')
      || item.querySelector('img:not(.product-media-item__current)')
      || item.querySelector('img');
  };

  const getLargestImageUrl = (image) => {
    const srcset = image?.getAttribute('srcset') || '';
    if (!srcset) return image?.currentSrc || image?.src || '';

    const candidates = srcset.split(',')
      .map((entry) => {
        const parts = entry.trim().split(/\s+/);
        const url = parts[0] || '';
        const width = Number((parts[1] || '').replace('w', '')) || 0;
        return { url, width };
      })
      .filter((candidate) => candidate.url);

    if (!candidates.length) return image?.currentSrc || image?.src || '';
    candidates.sort((a, b) => b.width - a.width);
    return candidates[0].url;
  };

  const applyZoomTransform = () => {
    if (!zoomImage) return;
    zoomImage.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`;
    if (overlay) overlay.dataset.zoomed = scale > 1.02 ? 'true' : 'false';
  };

  const resetZoom = () => {
    scale = 1;
    translateX = 0;
    translateY = 0;
    applyZoomTransform();
  };

  const setScaleAtPoint = (nextScale, pointX, pointY) => {
    if (!zoomImage || !viewport) return;

    const rect = viewport.getBoundingClientRect();
    const localX = pointX - rect.left - rect.width / 2;
    const localY = pointY - rect.top - rect.height / 2;
    const previousScale = scale;

    scale = clamp(nextScale, MIN_ZOOM, MAX_ZOOM);

    if (previousScale !== scale) {
      const ratio = scale / previousScale;
      translateX = localX - (localX - translateX) * ratio;
      translateY = localY - (localY - translateY) * ratio;
    }

    if (scale <= 1.02) {
      translateX = 0;
      translateY = 0;
    }

    applyZoomTransform();
  };

  const closeLightbox = () => {
    if (!overlay) return;

    const closingOverlay = overlay;
    closingOverlay.classList.remove('is-open');
    closingOverlay.classList.add('is-closing');

    window.setTimeout(() => {
      if (closingOverlay.parentNode) closingOverlay.remove();
      if (overlay === closingOverlay) {
        overlay = null;
        viewport = null;
        zoomImage = null;
      }
      activePointers.clear();
      document.documentElement.classList.remove('zed-product-zoom-open');
    }, 260);
  };

  const buildLightbox = () => {
    const element = document.createElement('div');
    element.className = 'zed-product-lightbox';
    element.setAttribute('role', 'dialog');
    element.setAttribute('aria-modal', 'true');
    element.setAttribute('aria-label', 'Expanded product image');
    element.innerHTML = `
      <button type="button" class="zed-product-lightbox__close" aria-label="Close expanded product image">×</button>
      <div class="zed-product-lightbox__viewport">
        <img class="zed-product-lightbox__image" alt="">
      </div>
      <div class="zed-product-lightbox__hint">Pinch, scroll, or drag to zoom</div>
    `;
    document.body.appendChild(element);

    overlay = element;
    viewport = element.querySelector('.zed-product-lightbox__viewport');
    zoomImage = element.querySelector('.zed-product-lightbox__image');

    element.querySelector('.zed-product-lightbox__close')?.addEventListener('click', closeLightbox);
    element.addEventListener('click', (event) => {
      if (event.target === element) closeLightbox();
    });

    viewport.addEventListener('wheel', (event) => {
      event.preventDefault();
      const factor = event.deltaY > 0 ? 0.88 : 1.14;
      setScaleAtPoint(scale * factor, event.clientX, event.clientY);
    }, { passive: false });

    viewport.addEventListener('dblclick', (event) => {
      event.preventDefault();
      if (scale > 1.02) resetZoom();
      else setScaleAtPoint(2.35, event.clientX, event.clientY);
    });

    viewport.addEventListener('pointerdown', (event) => {
      activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      viewport.setPointerCapture?.(event.pointerId);

      if (activePointers.size === 1) {
        lastPointer = { x: event.clientX, y: event.clientY };
      } else if (activePointers.size === 2) {
        const points = Array.from(activePointers.values());
        pinchStartDistance = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
        pinchStartScale = scale;
      }
    });

    viewport.addEventListener('pointermove', (event) => {
      if (!activePointers.has(event.pointerId)) return;
      activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (activePointers.size === 2) {
        event.preventDefault();
        const points = Array.from(activePointers.values());
        const distance = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
        const centerX = (points[0].x + points[1].x) / 2;
        const centerY = (points[0].y + points[1].y) / 2;
        if (pinchStartDistance > 0) {
          setScaleAtPoint(pinchStartScale * (distance / pinchStartDistance), centerX, centerY);
        }
        return;
      }

      if (activePointers.size === 1 && lastPointer && scale > 1.02) {
        event.preventDefault();
        translateX += event.clientX - lastPointer.x;
        translateY += event.clientY - lastPointer.y;
        lastPointer = { x: event.clientX, y: event.clientY };
        applyZoomTransform();
      }
    });

    const endPointer = (event) => {
      activePointers.delete(event.pointerId);
      if (activePointers.size === 0) {
        lastPointer = null;
      } else if (activePointers.size === 1) {
        const point = Array.from(activePointers.values())[0];
        lastPointer = { x: point.x, y: point.y };
      }
    };

    viewport.addEventListener('pointerup', endPointer);
    viewport.addEventListener('pointercancel', endPointer);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeLightbox();
    });
  };

  const openLightbox = (image) => {
    if (!image) return;
    if (!overlay) buildLightbox();

    overlay.classList.remove('is-open', 'is-closing');
    zoomImage.src = getLargestImageUrl(image);
    zoomImage.alt = image.alt || 'Expanded product image';
    resetZoom();
    document.documentElement.classList.add('zed-product-zoom-open');

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (overlay) overlay.classList.add('is-open');
      });
    });
  };

  document.addEventListener('click', (event) => {
    if (event.target.closest('.product-gallery-arrow')) return;
    if (event.target.closest('.zed-product-lightbox')) return;

    const isDesktop = window.matchMedia('(min-width: 861px)').matches;
    if (isDesktop) return;

    const item = event.target.closest('.template-product .product-media-item.is-primary-media:not(.is-hidden)');
    if (!item) return;

    const image = getVisibleMediaImage(item);
    if (!image) return;

    event.preventDefault();
    openLightbox(image);
  });
})();

// v179 contained desktop product hover zoom:
// Keep the visible mockup image/frame the same size. Hovering overlays a magnified view inside that same mockup space.
(() => {
  const desktopQuery = window.matchMedia('(min-width: 861px)');
  const ZOOM_SCALE = 2.08;

  const getActiveImage = (item) => {
    if (!item) return null;
    return item.querySelector('.product-media-item__incoming')
      || item.querySelector('img:not(.product-media-item__current)')
      || item.querySelector('img');
  };

  const getLargestImageUrl = (image) => {
    const srcset = image?.getAttribute('srcset') || '';
    if (!srcset) return image?.currentSrc || image?.src || '';

    const candidates = srcset.split(',')
      .map((entry) => {
        const parts = entry.trim().split(/\s+/);
        const url = parts[0] || '';
        const width = Number((parts[1] || '').replace('w', '')) || 0;
        return { url, width };
      })
      .filter((candidate) => candidate.url);

    if (!candidates.length) return image?.currentSrc || image?.src || '';
    candidates.sort((a, b) => b.width - a.width);
    return candidates[0].url;
  };

  const escapeCssUrl = (value) => String(value || '').replace(/["\\]/g, '\\$&');

  const getRenderedImageRect = (item) => {
    const image = getActiveImage(item);
    if (!item || !image) return null;

    const boxRect = item.getBoundingClientRect();
    if (!boxRect.width || !boxRect.height) return boxRect;

    if (image.tagName !== 'IMG' || !image.naturalWidth || !image.naturalHeight) {
      return boxRect;
    }

    const objectFit = window.getComputedStyle(image).objectFit || 'fill';
    if (objectFit !== 'contain' && objectFit !== 'scale-down') {
      return boxRect;
    }

    const imageRatio = image.naturalWidth / image.naturalHeight;
    const boxRatio = boxRect.width / boxRect.height;

    let renderedWidth = boxRect.width;
    let renderedHeight = boxRect.height;

    if (boxRatio > imageRatio) {
      renderedHeight = boxRect.height;
      renderedWidth = boxRect.height * imageRatio;
    } else {
      renderedWidth = boxRect.width;
      renderedHeight = boxRect.width / imageRatio;
    }

    return {
      left: boxRect.left + (boxRect.width - renderedWidth) / 2,
      right: boxRect.left + (boxRect.width + renderedWidth) / 2,
      top: boxRect.top + (boxRect.height - renderedHeight) / 2,
      bottom: boxRect.top + (boxRect.height + renderedHeight) / 2,
      width: renderedWidth,
      height: renderedHeight
    };
  };

  const clearZoomState = (item) => {
    if (!item) return;
    item.classList.remove('is-desktop-hover-zoom-active');
    item.classList.remove('is-desktop-hover-zoom-cursorless');
  };

  const isPointerInsideImage = (item, event) => {
    const rect = getRenderedImageRect(item);
    if (!rect) return false;
    return (
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom
    );
  };

  const setZoomPoint = (item, event) => {
    const image = getActiveImage(item);
    const rect = getRenderedImageRect(item);
    const itemRect = item.getBoundingClientRect();

    if (!image || !rect || !rect.width || !rect.height || !itemRect.width || !itemRect.height) return false;

    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    const imageUrl = getLargestImageUrl(image);

    item.style.setProperty('--zed-hover-zoom-x', `${Math.min(Math.max(x, 0), 100)}%`);
    item.style.setProperty('--zed-hover-zoom-y', `${Math.min(Math.max(y, 0), 100)}%`);
    item.style.setProperty('--zed-hover-zoom-left', `${rect.left - itemRect.left}px`);
    item.style.setProperty('--zed-hover-zoom-top', `${rect.top - itemRect.top}px`);
    item.style.setProperty('--zed-hover-zoom-width', `${rect.width}px`);
    item.style.setProperty('--zed-hover-zoom-height', `${rect.height}px`);
    item.style.setProperty('--zed-hover-zoom-size', `${ZOOM_SCALE * 100}% ${ZOOM_SCALE * 100}%`);

    if (imageUrl) {
      item.style.setProperty('--zed-hover-zoom-image', `url("${escapeCssUrl(imageUrl)}")`);
    }

    return true;
  };

  const syncZoomState = (item, event) => {
    if (!item) return false;
    if (!isPointerInsideImage(item, event)) {
      clearZoomState(item);
      return false;
    }

    if (!setZoomPoint(item, event)) {
      clearZoomState(item);
      return false;
    }

    item.classList.add('is-desktop-hover-zoom-active');
    item.classList.add('is-desktop-hover-zoom-cursorless');
    return true;
  };

  document.addEventListener('pointerenter', (event) => {
    if (!desktopQuery.matches) return;

    const item = event.target.closest?.('.template-product .product-media-item.is-primary-media:not(.is-hidden)');
    if (!item) return;

    syncZoomState(item, event);
  }, true);

  document.addEventListener('pointermove', (event) => {
    if (!desktopQuery.matches) return;

    const item = event.target.closest?.('.template-product .product-media-item.is-primary-media:not(.is-hidden)');
    if (!item) return;

    syncZoomState(item, event);
  }, { passive: true, capture: true });

  document.addEventListener('pointerleave', (event) => {
    const item = event.target.closest?.('.template-product .product-media-item.is-primary-media');
    if (!item) return;

    clearZoomState(item);
  }, true);

  document.addEventListener('pointerdown', (event) => {
    if (!desktopQuery.matches) return;

    const item = event.target.closest?.('.template-product .product-media-item.is-primary-media:not(.is-hidden)');
    if (!item) return;
    if (!isPointerInsideImage(item, event)) return;

    event.preventDefault();
  }, true);
})();




